
import { defineComponent, ref, Ref, watch, onMounted, computed } from "vue";
import { Modal } from "bootstrap";
import useAlert from "@/composables/Alert";
import Button from "@/components/Button.vue"
import axios from "axios";
import { useCaminhaoClipboard } from '@/composables/useVehicleClipboard'
import Api from "@/services/Api";

export default defineComponent({
  name: "ModalInfoCaminhao",
  components: { Button },
  props: {
    // objeto com dados do veículo selecionado (espera { id, marca, ... })
    targetClickCar: Object,
  },
  setup(props) {
    const { showTimeAlert } = useAlert();
    const { copiarDadosTecnicos, colarDadosTecnicos, copiado } = useCaminhaoClipboard()
    // reactive refs
    const CarUpdated: Ref<any> = ref({
      id: '',
      codModelo: '',
      fipe: '',
      marca: '',
      ordem: '',
      prioridade: '',
      modelo: '',
      ano: '',
      ano2: '',
      combustivel: '',
      lubrificante_sae: '',
      carter_com_filtro: '',
      lubrificante_base: '',
      lubrificante_api: '',
      lubrificante_acea: '',
      lubrificante_oem: '',
      lubrificante_ilsac: '',
      manual: '',
      pneu_dianteiro_1: '',
      pneu_dianteiro_2: '',
      pneu_traseiro_1: '',
      pneu_traseiro_2: '',
      tipo_veiculo: 'pesado',
      array_combustivel: '',
      tracao: '',
      lubrificante_diferencial_sae: '',
      lubrificante_diferencial_base: '',
      lubrificante_diferencial_volume_dianteiro: '',
      lubrificante_diferencial_volume_traseiro_1_eixo: '',
      lubrificante_diferencial_volume_traseiro_2_eixo: '',
      lubrificante_diferencial_api: '',
      lubrificante_diferencial_mil: '',
      lubrificante_diferencial_oem: '',
      lubrificante_diferencial_obs: '',
    }); // dados exibidos/alterados no formulário
    const myCar: Ref<any> = ref({}); // identifica o veículo atual (id + marca)
    const loadSalvar: Ref<boolean> = ref(false); // controla spinner do botão
    const marcasDeVeiculos: Ref<any> = ref([]);
    const spanWarning: Ref<boolean> = ref(false);
    const infoTecnicasCopiado = ref({
      lubrificante_sae: '',
      carter_com_filtro: '',
      lubrificante_base: '',
      lubrificante_api: '',
      lubrificante_acea: '',
      lubrificante_oem: '',
      lubrificante_ilsac: '',
      pneu_dianteiro_1: '',
      pneu_dianteiro_2: '',
      pneu_traseiro_1: '',
      pneu_traseiro_2: '',
      lubrificante_diferencial_sae: '',
      lubrificante_diferencial_base: '',
      lubrificante_diferencial_volume_dianteiro: '',
      lubrificante_diferencial_volume_traseiro_1_eixo: '',
      lubrificante_diferencial_volume_traseiro_2_eixo: '',
      lubrificante_diferencial_api: '',
      lubrificante_diferencial_mil: '',
      lubrificante_diferencial_oem: '',
      lubrificante_diferencial_obs: '',
    });
    // DOM ref for this modal element
    const modalEl: Ref<HTMLElement | null> = ref(null);

    // retorna token armazenado (usado nas chamadas axios)
    const getToken = async () => localStorage.getItem("token_usuario");

    // quando o pai atualizar `targetClickCar`, atualiza `myCar` para disparar o refresh
    watch(() => props.targetClickCar, () => {
      myCar.value = {
        id: props.targetClickCar?.id,
        marca: props.targetClickCar?.marca,
      };
    });

    // quando `myCar` muda, busca os dados completos do modelo
    watch(() => myCar.value, () => refreshCarInfo());


    // Validação Ano Combustível (2025 Gasolina)
    const isAnoCombustivelValid = computed(() => {
      if (!CarUpdated.value.ano) return false;
      const regex = /^\d{4}\s+(Gasolina|Alcool|Álcool|Diesel|Flex|Híbrido|Elétrico)$/i;
      return regex.test(CarUpdated.value.ano);
    });

    // Validação FIPE (000000-0)
    const isFipeValid = computed(() => {
      if (!CarUpdated.value.fipe) return false;
      return /^\d{6}-\d{1}$/.test(CarUpdated.value.fipe);
    });

    // Funções de manipulação
    const validateOnlyNumbers = (val: string, field: string) => {
      CarUpdated.value[field] = val.replace(/\D/g, '');
    };

    const handleFipeInput = (val: string) => {
      let cleaned = val.replace(/\D/g, ""); 
      if (cleaned.length > 6) {
        cleaned = `${cleaned.slice(0, 6)}-${cleaned.slice(6, 7)}`;
      }
      CarUpdated.value.fipe = cleaned;
    };


    /**
     * Envia atualização do veículo para o backend.
     * Fecha o modal localmente e também chama `props.fechaModal()` se fornecida.
     */
    const updateSend = async (e: Event) => {
      e.preventDefault();

      // 1. Verificação básica de campos vazios (o que você já tinha)
      const camposVazios = !CarUpdated.value.codModelo || 
                          !CarUpdated.value.fipe || 
                          !CarUpdated.value.marca || 
                          !CarUpdated.value.modelo || 
                          !CarUpdated.value.combustivel || 
                          !CarUpdated.value.array_combustivel || 
                          !CarUpdated.value.ano || 
                          !CarUpdated.value.ano2;

      // Reutilizamos as computed properties que criamos (isFipeValid e isAnoCombustivelValid)
      
      // 2. Lógica de Bloqueio com verificações
      if (camposVazios) {
        showTimeAlert("Preencha todos os campos obrigatórios", 'error');
        return true
      } else if (!isFipeValid.value) {
          showTimeAlert("O Código FIPE digitado é inválido (use o padrão 000000-0)", 'error');
          return true
      } else if (!isAnoCombustivelValid.value) {
          showTimeAlert("O formato de Ano e Combustível está incorreto", 'error');
          return true
      } else if (CarUpdated.value.ano2.length < 4) {
          showTimeAlert("O formato do Ano está incorreto", 'error');
          return true
      }

      // Se passou em tudo
      spanWarning.value = false;

      try {
        loadSalvar.value = true;
        await axios.post(
          "atualizaDadosVeiculo",
          { ...CarUpdated.value },
          { headers: { Authorization: `Bearer ${await getToken()}` } }
        );

        showTimeAlert("Veículo atualizado com sucesso!");

        // Fecha o modal exibido — obtém a instância utilizada para mostrá-lo.
        // Se não existir, cria uma temporária para então fechá-la.
        const el = modalEl.value || document.getElementById("infoVeiculo");
        let inst: any = null;
        if (el) {
          inst = Modal.getInstance(el) || new Modal(el);
          inst.hide && inst.hide();
        }
      } catch (err) {
        showTimeAlert("Não foi possivel atualizar o veículo.", "error");
      } finally {
        loadSalvar.value = false;
        // atualiza os dados exibidos com retorno mais recente
        refreshCarInfo();
      }
    };

    //FUNCAO QUE BUSCA AS MARCAS DOS CARROS EM NOSSA BASE
    async function getMarcas() {
      try {
        const res = await axios.get("getCaminhaoMarcas", {
          headers: {
            Authorization: `Bearer ${await getToken()}`,
          },
        });
        marcasDeVeiculos.value = res.data;
      } catch (err) {
        console.log(err);
      }
    }

    // Não inicializamos/guardamos uma instância fixa aqui — o modal
    // pode ter sido aberto por um componente pai. Na hora de fechar
    // obtemos a instância real com `Modal.getInstance(el)`.

    /**
     * Busca informações completas do veículo (filtra pelo id)
     * e popula `CarUpdated` para exibir no formulário.
     */
    async function refreshCarInfo() {
      // const { marca } = myCar.value || {};
      const { id } = myCar.value || {};
      if (!id) return;
      try {
        const res = await Api.get("getCaminhaoById",{ id: id });
        const d = res.data.caminhao;
        if (!d || d.length === 0) return;

        CarUpdated.value = {
          id: d.id,
          codModelo: d.codModelo,
          fipe: d.fipe,
          marca: d.marca,
          ordem: d.ordem,
          prioridade: d.prioridade,
          modelo: d.modelo,
          ano: d.ano,
          ano2: d.ano2,
          combustivel: d.combustivel,
          lubrificante_sae: d.lubrificante_sae,
          carter_com_filtro: d.carter_com_filtro,
          lubrificante_base: d.lubrificante_base,
          lubrificante_api: d.lubrificante_api,
          lubrificante_acea: d.lubrificante_acea,
          lubrificante_oem: d.lubrificante_oem,
          lubrificante_ilsac: d.lubrificante_ilsac,
          manual: d.manual,
          pneu_dianteiro_1: d.pneu_dianteiro_1,
          pneu_dianteiro_2: d.pneu_dianteiro_2,
          pneu_traseiro_1: d.pneu_traseiro_1,
          pneu_traseiro_2: d.pneu_traseiro_2,
          tipo_veiculo: 'pesado',
          array_combustivel: d.array_combustivel,
          tracao: d.tracao,
          lubrificante_diferencial_sae: d.lubrificante_diferencial_sae,
          lubrificante_diferencial_base: d.lubrificante_diferencial_base,
          lubrificante_diferencial_volume_dianteiro: d.lubrificante_diferencial_volume_dianteiro,
          lubrificante_diferencial_volume_traseiro_1_eixo: d.lubrificante_diferencial_volume_traseiro_1_eixo,
          lubrificante_diferencial_volume_traseiro_2_eixo: d.lubrificante_diferencial_volume_traseiro_2_eixo,
          lubrificante_diferencial_api: d.lubrificante_diferencial_api,
          lubrificante_diferencial_mil: d.lubrificante_diferencial_mil,
          lubrificante_diferencial_oem: d.lubrificante_diferencial_oem,
          lubrificante_diferencial_obs: d.lubrificante_diferencial_obs,
        };
      } catch (err) {
        // log para debug mínimo (não exibe em produção)
        console.error("refreshCarInfo error:", err);
      }
    }


    // Função original, mas usando o store compartilhado
    function copiarDadosTecnicosDoVeiculo() {
      copiarDadosTecnicos(CarUpdated.value)
    }

    function colarDadosTecnicosNoVeiculo() {
      const dadosColados = colarDadosTecnicos()
      if (dadosColados) {
        Object.assign(CarUpdated.value, dadosColados)
        console.log('Veículo atualizado:', CarUpdated.value)
      }
    }
    
    onMounted(async() => {
      getMarcas()
    })

    return {
      updateSend,
      CarUpdated,
      loadSalvar,
      modalEl,
      marcasDeVeiculos,
      spanWarning,
      validateOnlyNumbers,
      handleFipeInput,
      isAnoCombustivelValid, 
      isFipeValid,
      copiarDadosTecnicosDoVeiculo,
      colarDadosTecnicosNoVeiculo,
      /*
      copiarDadosTecnicos,
      colarDadosTecnicos,
      */
      infoTecnicasCopiado,
    };
  },
});
